import pdfMake from "pdfmake/build/pdfmake";
import headerPortraitImage from '../../assets/header-portrait.png';
import footerPortraitImage from '../../assets/footer-portrait.png';
import preApproverEsign from '../../assets/priorac_esign.png';

function getItemTitle(item) {
    return `${item?.fname ?? ''} ${item?.mname ? item?.mname[0] + '.' : ''} ${item?.lname ?? ''}${item?.suffix ? ' ' + item?.suffix : ''}${item?.post_nominal ? ', ' + item?.post_nominal : ''}`;
}

function getRepCategory(category) {
    if (category == 'Res') return '(Res)';
    else if (category == 'Ext') return '(Ext)';
    else if (category == 'Prod') return '(Prod)';
    else if (category == 'Admin') return '(Admin)';
    else return '';
}

function getRepRole(role) {
    if (role == 'Member') return '(Member)';
    else if (role == 'Leader') return '(Leader)';
    else return '';
}

function getBase64Image(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = url;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = error => reject(error);
    });
}

function getEsignDataUrl(base64Value, contextLabel) {
    if (typeof base64Value !== 'string') {
        throw new Error(`${contextLabel} is missing an e-signature image.`);
    }

    const normalized = base64Value.replace(/\s/g, '');
    if (normalized.length < 40 || !/^[A-Za-z0-9+/=]+$/.test(normalized)) {
        throw new Error(`${contextLabel} has an invalid e-signature image.`);
    }

    return `data:image/png;base64,${normalized}`;
}

function buildWorkloadSection(courseLoadData, adminLoadData, repLoadData, workLoadData, workloadDetails, preApproverEsignBase64) {
    const facultyName = workLoadData.faculty_name || 'Unknown faculty';
    const arrayOfCourses = courseLoadData.map(course => [
        { text: course.code, alignment: 'center' },
        { text: course.title, alignment: 'center' },
        { text: course.unit, alignment: 'center' },
        { text: course.time, alignment: 'center' },
        { text: course.day, alignment: 'center' },
        { text: course.room, alignment: 'center' },
        { text: course.section_code, alignment: 'center' },
        { text: course.class_size, alignment: 'center' },
        { text: course.hours_lecture, alignment: 'center' },
        { text: course.hours_lab, alignment: 'center' },
        { text: course.actual_contact_teaching, alignment: 'center' },
        { text: course.eqv, alignment: 'center' },
        { text: course.total_eqv, alignment: 'center' },
    ]);

    const arrayOfAdmin = adminLoadData.length === 0
        ? [[
            { text: 'None', alignment: 'center' },
            { text: 'None', alignment: 'left' },
            { text: '0', alignment: 'center' },
            { text: '0', alignment: 'center' }
        ]]
        : adminLoadData.map(admin => [
            { text: admin.so_number, alignment: 'center' },
            { text: admin.description, alignment: 'left' },
            { text: admin.hours, alignment: 'center' },
            { text: admin.unit, alignment: 'center' },
        ]);

    const arrayOfRep = repLoadData.length === 0
        ? [[
            { text: 'None', alignment: 'center' },
            { text: 'None', alignment: 'left' },
            { text: '0', alignment: 'center' },
            { text: '0', alignment: 'center' }
        ]]
        : repLoadData.map(rep => [
            { text: rep.rep_title.so_number, alignment: 'center' },
            { text: `${rep.rep_title.title} ${getRepCategory(rep.rep_title.category)} ${getRepRole(rep.role)}`, alignment: 'left' },
            { text: rep.unit_loading, alignment: 'center' },
            { text: rep.unit_loading, alignment: 'center' },
        ]);

    let educationStack = [
        {
            text: [
                { text: "Bachelor's Degree: ", bold: false },
                { text: workLoadData.undergraduate, bold: true }
            ],
            alignment: 'left'
        }
    ];

    if (workLoadData.graduate) {
        educationStack.push({
            text: [
                { text: "Master's Degree: ", bold: false },
                { text: workLoadData.graduate, bold: true }
            ],
            alignment: 'left'
        });
    }

    if (workLoadData.post_graduate) {
        educationStack.push({
            text: [
                { text: "Doctoral/Professional Degree: ", bold: false },
                { text: workLoadData.post_graduate, bold: true }
            ],
            alignment: 'left'
        });
    }

    const educationTable = {
        layout: 'noBorders',
        style: 'table',
        table: {
            widths: ['20%', '65.5%'],
            body: [
                [
                    { text: 'Educational Preparation:', bold: false, alignment: 'left' },
                    { stack: educationStack }
                ]
            ]
        }
    };

    let teamTeachTable = null;
    if (workLoadData.team_teach_name) {
        teamTeachTable = {
            layout: 'noBorders',
            style: 'table2',
            table: {
                widths: ['20%', '65.5%'],
                body: [
                    [
                        { text: 'Team Teach: ', bold: false, alignment: 'left' },
                        {
                            stack: [
                                { text: [{ text: workLoadData.team_teach_name, bold: true }], alignment: 'left' },
                                { text: [{ text: "Bachelor's Degree: ", bold: false }, { text: workLoadData.team_teach_undergraduate, bold: true }], alignment: 'left' },
                                { text: [{ text: "Master's Degree: ", bold: false }, { text: workLoadData.team_teach_graduate, bold: true }], alignment: 'left' },
                                { text: [{ text: "Doctoral/Professional Degree: ", bold: false }, { text: workLoadData.team_teach_post_graduate ? workLoadData.team_teach_post_graduate : 'N/A', bold: true }], alignment: 'left' }
                            ]
                        }
                    ]
                ]
            }
        };
    }

    let MSLoad = null, GSLoad = null, GELoad = null, ProfEdLoad = null;

    if (workLoadData.ms_load_eqv != 0) {
        MSLoad = {
            layout: 'noBorders', style: 'table',
            table: {
                widths: ['*', '*'],
                body: [[
                    { text: [{ text: 'Total MS Load Equivalent: ', bold: false }, { text: '      ' + workLoadData.ms_load_eqv, bold: true }], noWrap: true },
                    { text: [{ text: 'Total No. MS of Students: ', bold: false }, { text: '      ' + workLoadData.ms_total_student, bold: true }], noWrap: true },
                ]]
            }
        };
    }

    if (workLoadData.gs_load_eqv != 0) {
        GSLoad = {
            layout: 'noBorders', style: 'table',
            table: {
                widths: ['*', '*'],
                body: [[
                    { text: [{ text: 'Total GS Load Equivalent: ', bold: false }, { text: '      ' + workLoadData.gs_load_eqv, bold: true }], noWrap: true },
                    { text: [{ text: 'Total No. GS of Students: ', bold: false }, { text: '      ' + workLoadData.gs_total_student, bold: true }], noWrap: true },
                ]]
            }
        };
    }

    if (workLoadData.ge_load_eqv != 0) {
        GELoad = {
            layout: 'noBorders', style: 'table',
            table: {
                widths: ['*', '*'],
                body: [[
                    { text: [{ text: 'Total GE Load Equivalent: ', bold: false }, { text: '      ' + workLoadData.ge_load_eqv, bold: true }], noWrap: true },
                    { text: [{ text: 'Total No. GE of Students: ', bold: false }, { text: '      ' + workLoadData.ge_total_student, bold: true }], noWrap: true },
                ]]
            }
        };
    }

    if (workLoadData.profed_load_eqv != 0) {
        ProfEdLoad = {
            layout: 'noBorders', style: 'table',
            table: {
                widths: ['*', '*'],
                body: [[
                    { text: [{ text: 'Total ProfEd Load Equivalent: ', bold: false }, { text: '      ' + workLoadData.profed_load_eqv, bold: true }], noWrap: true },
                    { text: [{ text: 'Total No. ProfEd of Students: ', bold: false }, { text: '      ' + workLoadData.profed_total_student, bold: true }], noWrap: true },
                ]]
            }
        };
    }

    return [
        {
            stack: [
                'FACULTY WORKLOAD',
                { text: workLoadData.semester + ' of SY ' + workLoadData.schoolyear, style: 'subheader' },
            ],
            style: 'header'
        },
        {
            layout: 'noBorders', style: 'table',
            table: {
                widths: ['*', '*', '*'],
                body: [[
                    { text: [{ text: 'Faculty: ', bold: false }, { text: workLoadData.faculty_name ? workLoadData.faculty_name.toUpperCase() : '', bold: true }], noWrap: true },
                    { text: [{ text: 'Employment Status: ', bold: false }, { text: workLoadData.employment_status, bold: true }], noWrap: true },
                    { text: [{ text: 'Designation: ', bold: false }, { text: workLoadData.designation, bold: true }], noWrap: false }
                ]]
            }
        },
        {
            layout: 'noBorders', style: 'table',
            table: {
                widths: ['*', '*', '*'],
                body: [[
                    { text: [{ text: 'Academic Rank: ', bold: false }, { text: workLoadData.rank, bold: true }], noWrap: true },
                    { text: [{ text: 'Institute: ', bold: false }, { text: workLoadData.institute, bold: true }], noWrap: false },
                    { text: [{ text: 'Department: ', bold: false }, { text: workLoadData.department, bold: true }], noWrap: false }
                ]]
            }
        },
        educationTable,
        teamTeachTable ? teamTeachTable : '',
        {
            layout: 'noBorders', style: 'table2',
            table: {
                widths: ['*', '*'],
                body: [[
                    { text: [{ text: 'Core Time: ', bold: false }, { text: workLoadData.core_time, bold: true }], noWrap: false },
                    { text: [{ text: 'Consultation Time: ', bold: false }, { text: workLoadData.consultation_time, bold: true }], noWrap: false },
                ]]
            }
        },
        {
            style: 'courseTable',
            table: {
                headerRows: 1,
                widths: [40, '*', 20, 50, 30, 37, 40, 22, 20, 20, 35, 20, 23],
                body: [
                    [
                        { text: 'Course No.', bold: true, alignment: 'center' },
                        { text: 'Course Title', bold: true, alignment: 'center' },
                        { text: 'Unit', bold: true, alignment: 'center' },
                        { text: 'Time', bold: true, alignment: 'center' },
                        { text: 'Day (online-in parenthesis)', bold: true, alignment: 'center' },
                        { text: 'Room', bold: true, alignment: 'center' },
                        { text: 'Program/Section', bold: true, alignment: 'center' },
                        { text: 'Class size', bold: true, alignment: 'center' },
                        { text: 'Hrs. Lec (a)', bold: true, alignment: 'center' },
                        { text: 'Hrs. Lab', bold: true, alignment: 'center' },
                        { text: 'Actual Contact Teaching Hrs/Wk', bold: true, alignment: 'center' },
                        { text: '.75 Eqv. (b)', bold: true, alignment: 'center' },
                        { text: 'Total Load Eqv.', bold: true, alignment: 'center' },
                    ],
                    ...arrayOfCourses
                ],
            },
            layout: { hLineWidth: () => 0.2, vLineWidth: () => 0.2 }
        },
        {
            layout: 'noBorders', style: 'table2',
            table: {
                widths: ['*', '*'],
                body: [[
                    { text: [{ text: 'Total Teaching Hours/Wk: ', bold: false }, { text: '      ' + workLoadData.total_teaching_hours, bold: true }], noWrap: true },
                    { text: [{ text: 'Total No. of Preparation: ', bold: false }, { text: '      ' + workLoadData.total_preparation, bold: true }], noWrap: true },
                ]]
            }
        },
        workLoadData.ms_load_eqv != 0 ? MSLoad : '',
        workLoadData.ge_load_eqv != 0 ? GELoad : '',
        workLoadData.profed_load_eqv != 0 ? ProfEdLoad : '',
        workLoadData.gs_load_eqv != 0 ? GSLoad : '',
        {
            style: 'loadTable',
            table: {
                headerRows: 1,
                widths: [35, '*', 24, 24],
                body: [
                    [
                        { text: 'SO No.', bold: true, alignment: 'center' },
                        { text: 'ADMIN LOAD RELEASE', bold: true, alignment: 'center' },
                        { text: 'Hour', bold: true, alignment: 'center' },
                        { text: 'Unit', bold: true, alignment: 'center' },
                    ],
                    ...arrayOfAdmin
                ],
            },
            layout: { hLineWidth: () => 0.2, vLineWidth: () => 0.2 }
        },
        {
            style: 'loadTable',
            table: {
                headerRows: 1,
                widths: [35, '*', 24, 24],
                body: [
                    [
                        { text: 'SO No.', bold: true, alignment: 'center' },
                        { text: 'RDE LOAD RELEASE', bold: true, alignment: 'center' },
                        { text: 'Hour', bold: true, alignment: 'center' },
                        { text: 'Unit', bold: true, alignment: 'center' },
                    ],
                    ...arrayOfRep
                ],
            },
            layout: { hLineWidth: () => 0.2, vLineWidth: () => 0.2 }
        },
        {
            layout: 'noBorders', style: 'table2',
            table: {
                widths: ['*'],
                body: [[
                    {
                        text: [
                            { text: 'TOTAL IREPA', bold: true },
                            { text: ' (Instruction, Research, Extension, Production and Administrative)', bold: false },
                            { text: ' LOAD UNITS/WK :', bold: true },
                            { text: '      ' + workLoadData.total_irepa_units, bold: true }
                        ],
                        noWrap: true
                    },
                ]]
            }
        },
        {
            layout: 'noBorders', style: 'table',
            table: {
                widths: ['*'],
                body: [[
                    {
                        text: [
                            { text: 'TOTAL IREPA', bold: true },
                            { text: ' (Instruction, Research, Extension, Production and Administrative)', bold: false },
                            { text: ' LOAD HOURS/WK :', bold: true },
                            { text: '      ' + workLoadData.total_irepa_hours, bold: true }
                        ],
                        noWrap: true
                    },
                ]]
            }
        },
        {
            layout: 'noBorders', style: 'esign',
            table: {
                dontBreakRows: true,
                widths: ['*', '*'],
                body: [[
                    {
                        stack: [
                            { text: 'Prepared By:', margin: [0, 0, 0, -5] },
                            ...workloadDetails.preparers.map(preparer => ({
                                stack: [
                                    { image: getEsignDataUrl(preparer.esign?.b64_name, `${facultyName}: Preparer ${getItemTitle(preparer)}`), width: 60, height: 40, alignment: 'left', margin: [0, 0, 0, -10] },
                                    { text: getItemTitle(preparer), bold: true },
                                    { text: preparer.designation || '', margin: [0, 0, 0, 5] }
                                ]
                            }))
                        ],
                        alignment: 'left'
                    },
                    {
                        stack: [
                            { text: 'Recommending Approval:', margin: [0, 0, 0, -5] },
                            ...workloadDetails.recommenders.map(recommender => ({
                                stack: [
                                    { image: getEsignDataUrl(recommender.esign?.b64_name, `${facultyName}: Recommender ${getItemTitle(recommender)}`), width: 60, height: 40, alignment: 'left', margin: [0, 0, 0, -10] },
                                    { text: getItemTitle(recommender), bold: true },
                                    { text: recommender.designation || '', margin: [0, 0, 0, 5] }
                                ]
                            }))
                        ],
                        alignment: 'left'
                    }
                ]]
            }
        },
        {
            layout: 'noBorders', style: 'esign2',
            table: {
                dontBreakRows: true,
                widths: ['*', 43],
                body: [[
                    {
                        stack: [
                            { text: 'Approved By:', margin: [0, 0, 0, -5] },
                            { image: getEsignDataUrl(workloadDetails.approver?.esign?.b64_name, `${facultyName}: Approver ${getItemTitle(workloadDetails.approver)}`), width: 60, height: 40, alignment: 'left', margin: [0, 0, 0, 0] },
                            { text: getItemTitle(workloadDetails.approver), bold: true },
                            { text: workloadDetails.approver.designation }
                        ],
                        alignment: 'left',
                        margin: [0, 0, 0, 0]
                    },
                    {
                        stack: [
                            { image: preApproverEsignBase64, width: 30, height: 30, alignment: 'left', margin: [-210, 45, 0, 0] },
                        ]
                    },
                ]]
            }
        },
        {
            layout: 'noBorders',
            style: { fontSize: 7, margin: [0, 0, 0, 0] },
            table: {
                dontBreakRows: true,
                widths: [80],
                body: [[
                    {
                        stack: [
                            { text: 'Date Printed:', bold: true, margin: [0, 10, 0, -5] },
                            { text: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), margin: [0, 10, 0, 0] }
                        ],
                        alignment: 'left',
                        margin: [0, 0, 0, 0]
                    }
                ]]
            }
        }
    ];
}

export async function exportAllApprovedPDF(allWorkloadsData) {
    const headerImageBase64 = await getBase64Image(headerPortraitImage);
    const footerImageBase64 = await getBase64Image(footerPortraitImage);
    const preApproverEsignBase64 = await getBase64Image(preApproverEsign);

    const allContent = [];

    for (let i = 0; i < allWorkloadsData.length; i++) {
        const { courseLoadData, adminLoadData, repLoadData, workLoadData, workloadDetails } = allWorkloadsData[i];
        const sectionContent = buildWorkloadSection(courseLoadData, adminLoadData, repLoadData, workLoadData, workloadDetails, preApproverEsignBase64);

        if (i === 0) {
            allContent.push(...sectionContent);
        } else {
            allContent.push({ stack: sectionContent, pageBreak: 'before' });
        }
    }

    const docDefinition = {
        defaultStyle: {
            font: "ArialNarrow"
        },
        info: {
            title: 'All Approved Faculty Workloads',
        },
        pageSize: {
            width: 612,
            height: 936
        },
        pageOrientation: 'portrait',
        pageMargins: [35, 110, 35, 95],

        header: (currentPage, pageCount, pageSize) => ({
            stack: [
                {
                    image: headerImageBase64,
                    width: pageSize.width - 40,
                    alignment: 'center',
                    margin: [0, 8, 0, 0]
                },
                {
                    text: 'Office of the Vice President for Academic Affairs',
                    fontSize: 16,
                    bold: true,
                    alignment: 'center',
                    font: "CenturySchoolBook"
                }
            ]
        }),

        footer: (currentPage, pageCount, pageSize) => ({
            image: footerImageBase64,
            width: pageSize.width - 40,
            alignment: 'center',
            margin: [0, 25, 0, 0]
        }),

        content: allContent,

        styles: {
            header: {
                fontSize: 15,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            subheader: {
                fontSize: 10
            },
            table: {
                fontSize: 9,
                margin: [0, 0, 0, 0]
            },
            table2: {
                fontSize: 9,
                margin: [0, 2, 0, 0]
            },
            courseTable: {
                fontSize: 7.5,
                margin: [0, 2, 0, 0]
            },
            loadTable: {
                fontSize: 7.5,
                margin: [0, 5, 0, 0]
            },
            esign: {
                fontSize: 9,
                margin: [60, 10, 0, 0]
            },
            esign2: {
                fontSize: 9,
                margin: [180, 10, 0, 0]
            }
        }
    };

    pdfMake.createPdf(docDefinition).open();
}
